import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
	emailInvoice,
	getDocuments,
	getInvoiceById,
	sendInvoice,
} from "../../lib/api";
import type { InvoiceRecord } from "../jobs/types";
import { DocumentRow } from "./DocumentRows";
import { InvoiceEditDialog } from "./InvoiceEditDialog";
import { RegisterSummary } from "./RegisterSummary";
import { RegisterToolbar } from "./RegisterToolbar";
import {
	DEFAULT_FILTERS,
	filterDocuments,
	groupDocuments,
	isFiltered,
	sortDocuments,
	summariseInvoices,
	type KindFilter,
	type RegisterFilters,
	type SortKey,
	type StatusFilter,
} from "./lib/registerFilters";
import styles from "./DocumentsPage.module.css";
import type { DocumentListRecord } from "./types";

function message(error: unknown, fallback: string): string {
	return error instanceof Error ? error.message : fallback;
}

/**
 * /invoices — the vendor register, and the place invoices are worked from.
 *
 * Every invoice can be opened in place: its line items, its dates and its
 * payments panel live in a detail row under it, and the actions that change it
 * (send, email, edit, take a payment) sit on the row itself. Before this the
 * page listed documents and linked to PDFs; correcting an invoice meant finding
 * the job it belonged to, and a jobless invoice had no route to it at all.
 *
 * The page owns all the state: the register list, the filter/sort settings, the
 * per-invoice detail cache and which row is open. The child components are
 * presentational, and the filtering itself is pure functions in
 * lib/registerFilters so it can be reasoned about without a DOM.
 */
export function DocumentsPage() {
	const [searchParams, setSearchParams] = useSearchParams();

	const [documents, setDocuments] = useState<DocumentListRecord[] | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const [searchInput, setSearchInput] = useState("");
	const [filters, setFilters] = useState<RegisterFilters>(DEFAULT_FILTERS);
	const [sort, setSort] = useState<SortKey>("newest");
	const [grouped, setGrouped] = useState(true);

	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [details, setDetails] = useState<Record<string, InvoiceRecord>>({});
	const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
	const [autoFocusAmountId, setAutoFocusAmountId] = useState<string | null>(null);

	const [busyId, setBusyId] = useState<string | null>(null);
	const [pendingSendId, setPendingSendId] = useState<string | null>(null);
	const [editing, setEditing] = useState<{
		document: DocumentListRecord;
		invoice: InvoiceRecord;
	} | null>(null);

	const refresh = useCallback(async (): Promise<void> => {
		const data = await getDocuments();
		setDocuments(data.documents);
	}, []);

	useEffect(() => {
		let active = true;
		getDocuments()
			.then((data) => {
				if (active) setDocuments(data.documents);
			})
			.catch((error: unknown) => {
				if (active) setLoadError(message(error, "Failed to load documents"));
			});
		return () => {
			active = false;
		};
	}, []);

	// The builder navigates here with the number it just created, so the page can
	// say which draft landed rather than leaving the user to find it.
	useEffect(() => {
		const created = searchParams.get("created");
		if (!created) return;
		setNotice(`${created} created as a draft.`);
		const next = new URLSearchParams(searchParams);
		next.delete("created");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	const report = (text: string): void => {
		setNotice(text);
		setActionError(null);
	};

	/** Runs a row action with that row marked busy, reporting either outcome. */
	const runAction = async (id: string, action: () => Promise<string>): Promise<void> => {
		setBusyId(id);
		setActionError(null);
		try {
			report(await action());
			await refresh();
		} catch (error) {
			setNotice(null);
			setActionError(message(error, "That action could not be completed"));
		} finally {
			setBusyId(null);
		}
	};

	const loadDetail = useCallback(async (invoiceId: string): Promise<void> => {
		try {
			const { invoice } = await getInvoiceById(invoiceId);
			setDetails((current) => ({ ...current, [invoiceId]: invoice }));
			setDetailErrors((current) => {
				const { [invoiceId]: _dropped, ...rest } = current;
				return rest;
			});
		} catch (error) {
			// A detail fetch that fails belongs in the row it failed for — blanking
			// the register over one row would be a much worse trade.
			setDetailErrors((current) => ({
				...current,
				[invoiceId]: message(error, "Could not load this invoice"),
			}));
		}
	}, []);

	const dropDetail = (invoiceId: string): void => {
		setDetails((current) => {
			const { [invoiceId]: _dropped, ...rest } = current;
			return rest;
		});
	};

	const toggleRow = (document: DocumentListRecord, focusAmount = false): void => {
		setAutoFocusAmountId(focusAmount ? document.id : null);
		setExpandedId((current) => {
			if (current === document.id && !focusAmount) return null;
			if (!details[document.id] && !detailErrors[document.id]) {
				void loadDetail(document.id);
			}
			return document.id;
		});
	};

	const openEditor = async (document: DocumentListRecord): Promise<void> => {
		const cached = details[document.id];
		if (cached) {
			setEditing({ document, invoice: cached });
			return;
		}
		setBusyId(document.id);
		setActionError(null);
		try {
			const { invoice } = await getInvoiceById(document.id);
			setDetails((current) => ({ ...current, [document.id]: invoice }));
			setEditing({ document, invoice });
		} catch (error) {
			setActionError(message(error, "Could not open this invoice"));
		} finally {
			setBusyId(null);
		}
	};

	const handleSendClick = (document: DocumentListRecord): void => {
		// Sending mints the client's link and hands over the number, so the first
		// click only arms the button (the house two-step).
		if (pendingSendId !== document.id) {
			setPendingSendId(document.id);
			return;
		}
		setPendingSendId(null);
		void runAction(document.id, async () => {
			await sendInvoice(document.id);
			dropDetail(document.id);
			return `${document.number} marked as sent.`;
		});
	};

	const handleEmail = (document: DocumentListRecord): void => {
		void runAction(document.id, async () => {
			const { recipients } = await emailInvoice(document.id);
			dropDetail(document.id);
			return `${document.number} emailed to ${recipients.join(", ")}.`;
		});
	};

	const handlePaymentRecorded = (document: DocumentListRecord): void => {
		dropDetail(document.id);
		void loadDetail(document.id);
		void refresh();
		report(`Payment recorded against ${document.number}.`);
	};

	const clearFilters = (): void => {
		setSearchInput("");
		setFilters(DEFAULT_FILTERS);
		setSort("newest");
		setGrouped(true);
	};

	const handleNeedsPayment = (): void => {
		const active = filters.kind === "invoice" && filters.status === "outstanding";
		setFilters((current) => ({
			...current,
			kind: active ? "all" : "invoice",
			status: active ? "all" : "outstanding",
		}));
	};

	const totals = useMemo(
		() => (documents ? summariseInvoices(documents) : null),
		[documents],
	);
	const visible = useMemo(() => {
		if (!documents) return [];
		return sortDocuments(filterDocuments(documents, filters), sort);
	}, [documents, filters, sort]);

	const groupingOn = grouped && sort === "newest";
	const groups = useMemo(
		() => (groupingOn ? groupDocuments(visible) : []),
		[groupingOn, visible],
	);
	const columnCount = groupingOn ? 6 : 7;
	const filtered = isFiltered(filters);

	const rowProps = (document: DocumentListRecord) => ({
		document,
		columnCount,
		showJobColumn: !groupingOn,
		expanded: expandedId === document.id,
		detail: details[document.id] ?? null,
		detailError: detailErrors[document.id] ?? null,
		autoFocusAmount: autoFocusAmountId === document.id,
		busy: busyId === document.id,
		sendArmed: pendingSendId === document.id,
		onToggle: (target: DocumentListRecord) => toggleRow(target),
		onEdit: (target: DocumentListRecord) => void openEditor(target),
		onSendClick: handleSendClick,
		onSendBlur: () => setPendingSendId(null),
		onEmail: handleEmail,
		onRecordPayment: (target: DocumentListRecord) => toggleRow(target, true),
		onPaymentRecorded: handlePaymentRecorded,
	});

	return (
		<div className={styles.root}>
			<div className={styles.head}>
				<div className={styles.headline}>
					<h1 className={styles.title}>Invoices</h1>
					<p className={styles.lede}>
						Every invoice and quote, linked back to its job where it has one. Open an
						invoice to see what it bills and to record a payment.
					</p>
				</div>
				<div className={styles.headActions}>
					<Link to="/invoices/create" className={styles.newButton}>
						New invoice
					</Link>
				</div>
			</div>

			<RegisterSummary totals={totals} filtered={filtered} />

			{loadError ? (
				<p className={styles.error} role="alert">
					{loadError}
				</p>
			) : (
				<>
					<RegisterToolbar
						searchInput={searchInput}
						onSearchInput={setSearchInput}
						onSearchSubmit={() =>
							setFilters((current) => ({ ...current, search: searchInput }))
						}
						onSearchClear={() => {
							setSearchInput("");
							setFilters((current) => ({ ...current, search: "" }));
						}}
						filters={filters}
						onKindChange={(kind: KindFilter) =>
							setFilters((current) => ({ ...current, kind }))
						}
						onStatusChange={(status: StatusFilter) =>
							setFilters((current) => ({ ...current, status }))
						}
						sort={sort}
						onSortChange={setSort}
						grouped={grouped}
						onGroupedChange={setGrouped}
						needsPayment={filters.kind === "invoice" && filters.status === "outstanding"}
						onNeedsPayment={handleNeedsPayment}
						shownCount={visible.length}
						totalCount={documents?.length ?? 0}
					/>

					{notice && !editing && (
						<p className={styles.notice} role="status">
							{notice}
						</p>
					)}
					{actionError && !editing && (
						<p className={styles.error} role="alert">
							{actionError}
						</p>
					)}

					{documents === null ? (
						<p className={styles.empty}>Loading invoices and quotes…</p>
					) : documents.length === 0 ? (
						<p className={styles.empty}>
							No invoices or quotes have been created yet.{" "}
							<Link to="/invoices/create" className={styles.inlineAction}>
								Create the first one
							</Link>
						</p>
					) : visible.length === 0 ? (
						<p className={styles.empty}>
							No documents match this search.{" "}
							<button type="button" className={styles.inlineAction} onClick={clearFilters}>
								Clear filters
							</button>
						</p>
					) : (
						<div className={styles.tableFrame}>
							<table className={styles.table}>
								<caption className={styles.caption}>All invoices and quotes</caption>
								<thead>
									<tr>
										<th scope="col">Document</th>
										{!groupingOn && <th scope="col">Job</th>}
										<th scope="col">Status</th>
										<th scope="col">Date</th>
										<th scope="col">Total</th>
										<th scope="col">Balance</th>
										<th scope="col">Actions</th>
									</tr>
								</thead>

								{groupingOn ? (
									groups.map((group) => (
										<tbody key={group.key} className={styles.group}>
											<tr className={styles.groupHeader}>
												<th colSpan={columnCount} scope="rowgroup">
													<div className={styles.groupHeading}>
														{group.jobId ? (
															<Link
																className={styles.jobLink}
																to={`/jobs?job=${encodeURIComponent(group.jobId)}`}
															>
																{group.jobRefInternal ||
																	"Job without internal reference"}
															</Link>
														) : (
															<span className={styles.jobLink}>
																{group.documents[0]?.number ?? "Invoice"}
																<span className={styles.standalone}> · no job</span>
															</span>
														)}
														<span className={styles.groupDetails}>
															<span>{group.clientName || "No client"}</span>
															<span aria-hidden="true">·</span>
															<span>{group.propertyAddress || "No property"}</span>
														</span>
													</div>
												</th>
											</tr>
											{group.documents.map((document) => (
												<DocumentRow
													key={`${document.kind}-${document.id}`}
													{...rowProps(document)}
												/>
											))}
										</tbody>
									))
								) : (
									<tbody>
										{visible.map((document) => (
											<DocumentRow
												key={`${document.kind}-${document.id}`}
												{...rowProps(document)}
											/>
										))}
									</tbody>
								)}
							</table>
						</div>
					)}
				</>
			)}

			{editing && (
				<InvoiceEditDialog
					document={editing.document}
					invoice={editing.invoice}
					onClose={() => setEditing(null)}
					onSaved={(saved) => {
						dropDetail(editing.document.id);
						if (expandedId === editing.document.id) {
							void loadDetail(editing.document.id);
						}
						setEditing(null);
						report(saved);
						void refresh();
					}}
					onEmailFailed={(failure) => {
						dropDetail(editing.document.id);
						setEditing(null);
						setNotice(null);
						setActionError(failure);
						void refresh();
					}}
				/>
			)}
		</div>
	);
}
